import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import {
  getBillingDashboard, getBillingSchoolYears, updateBillingCell,
  updateBillingStudentMeta, getBillingProration, uploadQbRegister,
  getQbUnmatched, saveQbUnmatched, dismissQbUnmatched,
  resolveQbUnmatched, splitQbUnmatched, applyDistrictQbPayment,
  getQbBatches, undoQbBatch, reverseQbUpload, deleteBillingStudentRow,
  mergeBillingDistricts,
} from "../lib/api";

const CATEGORY_COLOR = {
  red: "bg-red-100 text-red-800",
  green: "bg-green-100 text-green-800",
  yellow: "bg-yellow-100 text-yellow-800",
  purple: "bg-purple-100 text-purple-800",
  "": "",
};

const money = (v) =>
  v === null || v === undefined || v === "" ? "" :
  Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });

const STATUS_OPTIONS = ["", "F", "H", "SSS", "P", "—"];

export default function BillingDashboardPage() {
  const { isAdmin } = useAuth();
  const editable = isAdmin();
  const [schoolYears, setSchoolYears] = useState([]);
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [unmatched, setUnmatched] = useState([]);

  const loadUnmatched = useCallback((sy) => {
    getQbUnmatched(sy).then((r) => setUnmatched(r.data.unmatched || [])).catch(() => setUnmatched([]));
  }, []);

  const load = useCallback(async (sy) => {
    setLoading(true);
    try {
      const res = await getBillingDashboard(sy);
      setData(res.data);
      setYear(res.data.school_year);
      loadUnmatched(res.data.school_year);
    } catch (e) {
      toast.error("Could not load billing data.");
    } finally {
      setLoading(false);
    }
  }, [loadUnmatched]);

  useEffect(() => {
    getBillingSchoolYears().then((r) => {
      setSchoolYears(r.data.school_years);
      load(r.data.current);
    }).catch(() => load(null));
  }, [load]);

  // Optimistic cell update helper. Persists then reloads summary/balances.
  const saveCell = async (studentId, periodKey, patch, district) => {
    if (!editable) return;
    try {
      await updateBillingCell({ student_id: studentId, period_key: periodKey, school_year: year, district, ...patch });
      await load(year);
    } catch (e) {
      if (!e?.__readonly) toast.error("Save failed.");
    }
  };

  const saveMeta = async (studentId, patch, district) => {
    if (!editable) return;
    try {
      await updateBillingStudentMeta({ student_id: studentId, school_year: year, district, ...patch });
      setData((d) => ({
        ...d,
        students: d.students.map((s) =>
          s.student_id === studentId && s.district === district ? { ...s, ...patch } : s),
      }));
    } catch (e) {
      if (!e?.__readonly) toast.error("Save failed.");
    }
  };

  // Create an extra district row for a student who moved mid-year.
  const addDistrictRow = async (studentId, district, note) => {
    if (!editable) return;
    try {
      await updateBillingStudentMeta({ student_id: studentId, school_year: year, district, de_changes: note || "", notes: note || "" });
      await load(year);
      toast.success(`Added ${district} row.`);
    } catch (e) {
      if (!e?.__readonly) toast.error("Could not add district row.");
    }
  };

  // Delete an entire student-district billing line (e.g. one a QB upload wrongly created).
  const deleteRow = async (studentId, district, studentName) => {
    if (!editable) return;
    if (!window.confirm(`Delete the entire ${year} billing line for ${studentName}${district && district !== "—" ? ` (${district})` : ""}? This removes all periods for this row and cannot be undone.`)) return;
    try {
      await deleteBillingStudentRow(studentId, year, district);
      await load(year);
      toast.success(`Removed billing line for ${studentName}.`);
    } catch (e) {
      if (!e?.__readonly) toast.error("Could not delete the billing line.");
    }
  };

  if (loading && !data) {
    return <div className="p-8"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="billing-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#00305E]">Billing Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enrollment &amp; invoice summary — who was billed, what's been paid, and what's outstanding.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="billing-year-select"
            className="h-9 rounded-md border border-slate-300 px-3 text-sm"
            value={year || ""}
            onChange={(e) => load(e.target.value)}
          >
            {schoolYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {editable && (
            <button
              data-testid="qb-undo-open-btn"
              onClick={() => setUndoOpen(true)}
              className="h-9 rounded-md border border-amber-400 bg-amber-50 px-4 text-sm font-semibold text-amber-800 hover:bg-amber-100"
            >
              Undo QB Upload
            </button>
          )}
          {editable && (
            <button
              data-testid="qb-upload-open-btn"
              onClick={() => setUploadOpen(true)}
              className="h-9 rounded-md bg-[#00305E] px-4 text-sm font-semibold text-white hover:bg-[#00305E]/90"
            >
              Upload QB Sales Register
            </button>
          )}
        </div>
      </div>

      <RatesPanel rates={data.rates} />

      <DetailTable
        data={data}
        editable={editable}
        year={year}
        onSaveCell={saveCell}
        onSaveMeta={saveMeta}
        onAddDistrictRow={addDistrictRow}
        onDeleteRow={deleteRow}
      />

      <SummarySection data={data} />

      <DistrictBalances
        balances={data.district_balances}
        editable={editable}
        year={year}
        onDone={() => load(year)}
      />

      {editable && (
        <QbReviewSection
          rows={unmatched}
          year={year}
          students={data.students}
          periods={data.periods}
          districts={(data.district_balances?.districts || []).map((d) => d.district)}
          onChanged={() => load(year)}
          onRemoveLocal={(id) => setUnmatched((u) => u.filter((x) => x.id !== id))}
        />
      )}

      {uploadOpen && (
        <QbUploadDialog
          year={year}
          onClose={() => setUploadOpen(false)}
          onDone={() => load(year)}
        />
      )}

      {undoOpen && (
        <QbUndoDialog
          year={year}
          onClose={() => setUndoOpen(false)}
          onDone={() => load(year)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────── Rates panel ─────────────────────────── */
function RatesPanel({ rates }) {
  const rows = [
    { code: "F", meaning: "Full", sy: rates.f_school_year, summer: rates.f_summer },
    { code: "SSS", meaning: "Full + SSS", sy: rates.sss_school_year, summer: rates.sss_summer },
    { code: "H", meaning: "Half", sy: rates.h_school_year, summer: rates.h_summer },
  ];
  return (
    <div className="flex flex-wrap justify-end">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="billing-rates-panel">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-[#00305E]">Rates</h3>
          <span className="text-xs text-muted-foreground">Editable in Settings ▸ Billing Rates</span>
        </div>
        <table className="text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="pr-6 py-1">Status</th><th className="pr-6 py-1">Meaning</th>
              <th className="pr-6 py-1 text-right">School Year</th><th className="py-1 text-right">Summer</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-t border-slate-100">
                <td className="pr-6 py-1 font-semibold">{r.code}</td>
                <td className="pr-6 py-1">{r.meaning}</td>
                <td className="pr-6 py-1 text-right tabular-nums">{money(r.sy)}</td>
                <td className="py-1 text-right tabular-nums">{money(r.summer)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────── Detail table ─────────────────────────── */
function DetailTable({ data, editable, year, onSaveCell, onSaveMeta, onAddDistrictRow, onDeleteRow }) {
  const periods = data.periods;
  const [sortBy, setSortBy] = useState("student");
  const allDistricts = Array.from(new Set([
    ...data.students.map((s) => s.district),
    ...((data.district_balances?.districts || []).map((d) => d.district)),
  ].filter((d) => d && d !== "—"))).sort();

  // Default ("student") keeps the backend order (student name, then district).
  // "district" groups rows by district, then by student name within each district.
  const sortedStudents = useMemo(() => {
    const arr = [...data.students];
    if (sortBy === "district") {
      arr.sort((a, b) =>
        (a.district || "").toLowerCase().localeCompare((b.district || "").toLowerCase()) ||
        (a.student_name || "").toLowerCase().localeCompare((b.student_name || "").toLowerCase())
      );
    }
    return arr;
  }, [data.students, sortBy]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="p-3 border-b border-slate-200 flex flex-wrap items-start justify-between gap-3">
        <div>
        <h3 className="font-semibold text-[#00305E]">Student Enrollment &amp; Billing Detail</h3>
        <p className="text-xs text-muted-foreground">
          Paid cells: <span className="px-1 rounded bg-red-100 text-red-800">sent, unpaid</span>{" "}
          <span className="px-1 rounded bg-green-100 text-green-800">paid in full</span>{" "}
          <span className="px-1 rounded bg-yellow-100 text-yellow-800">partly paid</span>{" "}
          <span className="px-1 rounded bg-purple-100 text-purple-800">paid, not marked sent</span>
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          A student who moved districts mid-year shows one row per district — use <b>+ district</b> to add the new one and note the move (e.g. "Moved on 9/16").
        </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 shrink-0">
          <span className="font-semibold">Sort by</span>
          <select
            data-testid="billing-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-8 rounded-md border border-slate-300 px-2 text-xs"
          >
            <option value="student">Student (A–Z)</option>
            <option value="district">District</option>
          </select>
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-max text-xs border-collapse" data-testid="billing-detail-table">
          <thead>
            <tr className="bg-slate-50">
              <th className="sticky left-0 z-10 bg-slate-50 border border-slate-200 px-2 py-1 text-left min-w-[150px]" rowSpan={2}>Student</th>
              <th className="border border-slate-200 px-2 py-1 text-left" rowSpan={2}>DE Changes</th>
              <th className="border border-slate-200 px-2 py-1 text-left min-w-[110px]" rowSpan={2}>District</th>
              <th className="border border-slate-200 px-2 py-1 text-right" rowSpan={2}>Balance Due</th>
              {periods.map((p) => (
                <th key={p.key} className="border border-slate-200 px-2 py-1 text-center" colSpan={3}>{p.label}</th>
              ))}
            </tr>
            <tr className="bg-slate-50 text-[10px] text-muted-foreground">
              {periods.map((p) => (
                <FragmentHeader key={p.key} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedStudents.map((row, i) => {
              const prev = sortedStudents[i - 1];
              const sameStudentAsPrev = prev && prev.student_id === row.student_id;
              return (
                <StudentRow
                  key={row.row_key}
                  row={row}
                  periods={periods}
                  editable={editable}
                  showName={!sameStudentAsPrev}
                  allDistricts={allDistricts}
                  onSaveCell={onSaveCell}
                  onSaveMeta={onSaveMeta}
                  onAddDistrictRow={onAddDistrictRow}
                  onDeleteRow={onDeleteRow}
                />
              );
            })}
            {data.students.length === 0 && (
              <tr><td colSpan={4 + periods.length * 3} className="px-4 py-6 text-center text-muted-foreground">No enrolled students found for this year.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FragmentHeader() {
  return (
    <>
      <th className="border border-slate-200 px-1 py-0.5 font-normal">Status</th>
      <th className="border border-slate-200 px-1 py-0.5 font-normal">Sent</th>
      <th className="border border-slate-200 px-1 py-0.5 font-normal">Paid</th>
    </>
  );
}

function StudentRow({ row, periods, editable, showName, allDistricts, onSaveCell, onSaveMeta, onAddDistrictRow, onDeleteRow }) {
  const [adding, setAdding] = useState(false);
  const rid = row.row_key.replace(/[^a-zA-Z0-9]+/g, "-");
  const formerBadge = row.status && row.status.toLowerCase() !== "current";
  return (
    <tr className="hover:bg-slate-50/60">
      <td className={`sticky left-0 z-10 bg-white border border-slate-200 px-2 py-1 font-medium whitespace-nowrap ${showName ? "" : "text-slate-300"}`} data-testid={`billing-student-${rid}`}>
        {showName ? (
          <span>
            {row.student_name}
            {formerBadge && <span className="ml-1 text-[9px] uppercase tracking-wide text-amber-600">({row.status})</span>}
          </span>
        ) : <span className="pl-3">↳</span>}
      </td>
      <td className="border border-slate-200 px-1 py-1">
        {editable ? (
          <input
            data-testid={`de-changes-${rid}`}
            defaultValue={row.de_changes}
            key={row.de_changes}
            onBlur={(e) => e.target.value !== row.de_changes && onSaveMeta(row.student_id, { de_changes: e.target.value }, row.district)}
            className="w-24 rounded border border-transparent hover:border-slate-300 px-1 py-0.5 text-xs"
            title="Notes / changes (e.g. Moved on 9/16)"
          />
        ) : <span>{row.de_changes}</span>}
      </td>
      <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <span>{row.district}</span>
          {editable && row.is_primary_district && (
            <button
              data-testid={`add-district-${rid}`}
              onClick={() => setAdding(true)}
              className="text-[10px] text-[#00305E] underline hover:no-underline"
              title="Student moved districts — add another district row"
            >+ district</button>
          )}
          {editable && onDeleteRow && (
            <button
              data-testid={`delete-row-${rid}`}
              onClick={() => onDeleteRow(row.student_id, row.district, row.student_name)}
              className="text-[10px] text-red-600 underline hover:no-underline"
              title="Delete this entire billing line for the year"
            >delete line</button>
          )}
        </div>
        {adding && (
          <AddDistrictInline
            studentId={row.student_id}
            existingDistrict={row.district}
            allDistricts={allDistricts}
            onCancel={() => setAdding(false)}
            onAdd={(dist, note) => { onAddDistrictRow(row.student_id, dist, note); setAdding(false); }}
          />
        )}
      </td>
      <td className="border border-slate-200 px-2 py-1 text-right tabular-nums font-semibold" data-testid={`balance-${rid}`}>
        {row.balance_due ? money(row.balance_due) : "—"}
      </td>
      {periods.map((p) => (
        <PeriodCell
          key={p.key}
          studentId={row.student_id}
          district={row.district}
          rid={rid}
          period={p}
          cell={row.periods[p.key]}
          editable={editable}
          onSaveCell={onSaveCell}
        />
      ))}
    </tr>
  );
}

function AddDistrictInline({ studentId, existingDistrict, allDistricts, onCancel, onAdd }) {
  const [dist, setDist] = useState("");
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const options = allDistricts.filter((d) => d !== existingDistrict);
  const chosen = dist === "__custom__" ? custom.trim() : dist;
  return (
    <div className="mt-1 p-2 rounded border border-slate-300 bg-slate-50 space-y-1 min-w-[220px]" data-testid={`add-district-form-${studentId}`}>
      <select value={dist} onChange={(e) => setDist(e.target.value)}
              data-testid={`add-district-select-${studentId}`}
              className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs">
        <option value="">Select new district…</option>
        {options.map((d) => <option key={d} value={d}>{d}</option>)}
        <option value="__custom__">Other (type)…</option>
      </select>
      {dist === "__custom__" && (
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="District name"
               data-testid={`add-district-custom-${studentId}`}
               className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs" />
      )}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder='Note, e.g. "Moved on 9/16"'
             data-testid={`add-district-note-${studentId}`}
             className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs" />
      <div className="flex gap-1">
        <button disabled={!chosen} onClick={() => onAdd(chosen, note)}
                data-testid={`add-district-save-${studentId}`}
                className="flex-1 rounded bg-[#00305E] px-2 py-0.5 text-white text-[11px] disabled:opacity-40">Add</button>
        <button onClick={onCancel} className="rounded border border-slate-300 px-2 py-0.5 text-[11px]">Cancel</button>
      </div>
    </div>
  );
}

function PeriodCell({ studentId, district, rid, period, cell, editable, onSaveCell }) {
  const colorClass = CATEGORY_COLOR[cell.color] || "";
  const [busy, setBusy] = useState(false);

  const changeStatus = async (val) => {
    if (val === "P") {
      // Prefill an editable daily-rate proration suggestion.
      setBusy(true);
      let suggested = cell.partial_amount || 0;
      try {
        const r = await getBillingProration(studentId, period.key);
        suggested = r.data.suggested_amount;
        toast.info(`Suggested daily-rate charge: ${money(suggested)} (${r.data.enrolled_school_days}/${r.data.total_school_days} school days). Edit if discounting.`);
      } catch { /* ignore */ }
      setBusy(false);
      onSaveCell(studentId, period.key, { status: "P", partial_amount: suggested }, district);
    } else {
      onSaveCell(studentId, period.key, { status: val }, district);
    }
  };

  return (
    <>
      <td className="border border-slate-200 px-0.5 py-0.5 text-center">
        {editable ? (
          <select
            data-testid={`status-${rid}-${period.key}`}
            value={cell.status || ""}
            disabled={busy}
            onChange={(e) => changeStatus(e.target.value)}
            className="w-12 rounded border border-transparent hover:border-slate-300 bg-transparent text-xs"
          >
            {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o || "·"}</option>)}
          </select>
        ) : <span>{cell.status || ""}</span>}
        {cell.status === "P" && (
          editable ? (
            <input
              data-testid={`partial-${rid}-${period.key}`}
              type="number"
              defaultValue={cell.partial_amount ?? ""}
              key={cell.partial_amount}
              onBlur={(e) => {
                const v = e.target.value === "" ? 0 : Number(e.target.value);
                if (v !== (cell.partial_amount ?? "")) onSaveCell(studentId, period.key, { partial_amount: v }, district);
              }}
              className="mt-0.5 w-16 rounded border border-slate-300 px-1 text-[10px] text-right"
              title="Daily-rate amount (editable to discount)"
            />
          ) : <div className="text-[10px] text-right tabular-nums">{money(cell.partial_amount)}</div>
        )}
      </td>
      <td className="border border-slate-200 px-0.5 py-0.5 text-center">
        {editable ? (
          <input
            data-testid={`sent-${rid}-${period.key}`}
            type="checkbox"
            checked={cell.sent}
            onChange={(e) => onSaveCell(studentId, period.key, { sent: e.target.checked }, district)}
          />
        ) : (cell.sent ? "x" : "")}
      </td>
      <td className={`border border-slate-200 px-0.5 py-0.5 text-right tabular-nums ${colorClass}`}
          title={cell.invoice_number ? `Invoice #${cell.invoice_number}${cell.payment_date ? " · paid " + cell.payment_date : ""}` : ""}>
        {editable ? (
          <input
            data-testid={`paid-${rid}-${period.key}`}
            type="number"
            defaultValue={cell.paid_amount || ""}
            key={cell.paid_amount}
            onBlur={(e) => {
              const v = e.target.value === "" ? 0 : Number(e.target.value);
              if (v !== cell.paid_amount) onSaveCell(studentId, period.key, { paid_amount: v }, district);
            }}
            className={`w-20 rounded border border-transparent hover:border-slate-300 bg-transparent px-1 text-right text-xs ${colorClass}`}
          />
        ) : (cell.paid_amount ? money(cell.paid_amount) : "")}
      </td>
    </>
  );
}

/* ─────────────────────────── Monthly summary ─────────────────────────── */
function SummarySection({ data }) {
  const periods = data.periods;
  const s = data.summary;
  const pct = (v) => (v ? (v * 100).toFixed(1) + "%" : "—");

  const countRow = (label, key, testid) => (
    <tr data-testid={testid}>
      <td className="border border-slate-200 px-2 py-1 font-medium">{label}</td>
      {periods.map((p) => (
        <td key={p.key} className="border border-slate-200 px-2 py-1 text-center tabular-nums">
          {s.counts[p.key][key]}
        </td>
      ))}
    </tr>
  );
  const moneyRow = (label, obj, testid, cls = "") => (
    <tr data-testid={testid} className={cls}>
      <td className="border border-slate-200 px-2 py-1 font-medium">{label}</td>
      {periods.map((p) => (
        <td key={p.key} className="border border-slate-200 px-2 py-1 text-right tabular-nums">
          {obj[p.key] ? money(obj[p.key]) : "—"}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="p-3 border-b border-slate-200">
        <h3 className="font-semibold text-[#00305E]">Monthly Summary</h3>
        <p className="text-xs text-muted-foreground">Auto-calculated from the detail table above.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-max text-xs" data-testid="billing-summary-table">
          <thead>
            <tr className="bg-slate-50">
              <th className="border border-slate-200 px-2 py-1 text-left min-w-[190px]">&nbsp;</th>
              {periods.map((p) => (
                <th key={p.key} className="border border-slate-200 px-2 py-1 text-center min-w-[80px]">{p.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {countRow("Full (F)", "F", "summary-full")}
            {countRow("Full + SSS", "SSS", "summary-sss")}
            {countRow("Half (H)", "H", "summary-half")}
            {countRow("Partial / Daily", "P", "summary-partial")}
            {countRow("Not Enrolled (—)", "NE", "summary-not-enrolled")}
            <tr data-testid="summary-total-enrolled" className="bg-slate-50 font-semibold">
              <td className="border border-slate-200 px-2 py-1">Total Enrolled</td>
              {periods.map((p) => (
                <td key={p.key} className="border border-slate-200 px-2 py-1 text-center tabular-nums">{s.total_enrolled[p.key]}</td>
              ))}
            </tr>
            <tr data-testid="summary-fte" className="bg-slate-100 font-semibold">
              <td className="border border-slate-200 px-2 py-1">Enrollment Number (FTE)</td>
              {periods.map((p) => (
                <td key={p.key} className="border border-slate-200 px-2 py-1 text-center tabular-nums">{s.fte[p.key]}</td>
              ))}
            </tr>
            <tr><td colSpan={periods.length + 1} className="border border-slate-200 bg-[#00305E]/5 px-2 py-1 font-semibold text-[#00305E]">Billing</td></tr>
            {moneyRow("Projected (should total)", s.projected, "summary-projected")}
            {moneyRow("Sent", s.sent, "summary-sent")}
            {moneyRow("Sent vs Projected", s.sent_vs_projected, "summary-sent-vs-projected")}
            {moneyRow("Paid", s.paid, "summary-paid")}
            <tr data-testid="summary-pct-collected">
              <td className="border border-slate-200 px-2 py-1 font-medium">% of Sent Collected</td>
              {periods.map((p) => (
                <td key={p.key} className="border border-slate-200 px-2 py-1 text-right tabular-nums">{pct(s.pct_collected[p.key])}</td>
              ))}
            </tr>
            {moneyRow("Unpaid From This Month", s.unpaid_this_month, "summary-unpaid-this-month")}
            {moneyRow("Carried Over From Prior Months", s.carried_over, "summary-carried-over")}
            {moneyRow("Total Outstanding To Date", s.total_outstanding, "summary-total-outstanding", "bg-amber-50 font-semibold")}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────── District balances ─────────────────────────── */
function DistrictBalances({ balances, editable, year, onDone }) {
  const [mergeOpen, setMergeOpen] = useState(false);
  const hasApplied = (balances.payments_applied_total || 0) > 0;
  return (
    <div className="max-w-xl rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="p-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#00305E]">Balance Due by District</h3>
          {hasApplied && <p className="text-[11px] text-muted-foreground">Includes district-level payments applied from QuickBooks.</p>}
        </div>
        {editable && (balances.districts || []).length > 1 && (
          <button
            data-testid="merge-districts-open-btn"
            onClick={() => setMergeOpen(true)}
            className="h-8 shrink-0 rounded-md border border-[#00305E] px-3 text-xs font-semibold text-[#00305E] hover:bg-[#00305E]/5"
          >
            Merge districts
          </button>
        )}
      </div>
      <table className="w-full text-sm" data-testid="billing-district-balances">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="px-4 py-1">District</th>
            {hasApplied && <th className="px-4 py-1 text-right">Payments Applied</th>}
            <th className="px-4 py-1 text-right">Balance Due</th>
          </tr>
        </thead>
        <tbody>
          {balances.districts.map((d) => (
            <tr key={d.district} className="border-t border-slate-100">
              <td className="px-4 py-1">{d.district}</td>
              {hasApplied && <td className="px-4 py-1 text-right tabular-nums text-green-700">{d.payments_applied ? money(d.payments_applied) : ""}</td>}
              <td className="px-4 py-1 text-right tabular-nums">{money(d.balance_due)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300 font-bold bg-slate-50">
            <td className="px-4 py-1">Total</td>
            {hasApplied && <td className="px-4 py-1 text-right tabular-nums text-green-700">{money(balances.payments_applied_total)}</td>}
            <td className="px-4 py-1 text-right tabular-nums" data-testid="district-balance-total">{money(balances.total)}</td>
          </tr>
        </tbody>
      </table>
      {mergeOpen && (
        <MergeDistrictsDialog
          year={year}
          districts={(balances.districts || []).map((d) => d.district)}
          onClose={() => setMergeOpen(false)}
          onDone={onDone}
        />
      )}
    </div>
  );
}

/* ─────────────────────── Merge districts dialog ─────────────────────── */
function MergeDistrictsDialog({ year, districts, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [into, setInto] = useState("");
  const [allYears, setAllYears] = useState(true);
  const [busy, setBusy] = useState(false);

  const toggle = (d) => setSelected((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  const sources = selected.filter((d) => d !== into);

  const doMerge = async () => {
    if (!into) { toast.error("Pick the name to keep."); return; }
    if (sources.length === 0) { toast.error("Select at least one other district to merge in."); return; }
    if (!window.confirm(`Merge ${sources.map((s) => `"${s}"`).join(", ")} into "${into}"${allYears ? " across all school years" : ` for ${year}`}? This renames those billing lines and can't be auto-undone.`)) return;
    setBusy(true);
    try {
      const r = await mergeBillingDistricts({ into, from: sources, all_years: allYears, school_year: year });
      const d = r.data;
      toast.success(`Merged into "${into}" — ${d.renamed.records} line(s) renamed, ${d.merged_cells} duplicate(s) combined.`);
      onDone();
      onClose();
    } catch (e) {
      if (!e?.__readonly) toast.error(e?.response?.data?.detail || "Merge failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="merge-districts-dialog">
      <div className="w-full max-w-md max-h-[88vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#00305E]">Merge Districts</h3>
          <button onClick={onClose} data-testid="merge-districts-close-btn" className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          QuickBooks sometimes writes the same district under slightly different names. Tick the variants that are the same district, then choose the one name to keep.
        </p>

        <div className="space-y-1 mb-4 max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid="merge-districts-list">
          {districts.map((d) => (
            <label key={d} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-slate-50 cursor-pointer text-sm">
              <input
                type="checkbox"
                data-testid={`merge-src-${d.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                checked={selected.includes(d)}
                onChange={() => toggle(d)}
              />
              <span>{d}</span>
            </label>
          ))}
        </div>

        <label className="block text-xs font-semibold text-slate-600 mb-1">Keep this name</label>
        <select
          data-testid="merge-into-select"
          value={into}
          onChange={(e) => setInto(e.target.value)}
          className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm mb-3"
        >
          <option value="">— choose the name to keep —</option>
          {(selected.length ? selected : districts).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
          <input type="checkbox" data-testid="merge-all-years" checked={allYears} onChange={(e) => setAllYears(e.target.checked)} />
          <span>Apply to all school years (recommended)</span>
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-9 rounded-md border border-slate-300 px-4 text-sm">Cancel</button>
          <button
            onClick={doMerge}
            disabled={busy}
            data-testid="merge-districts-submit"
            className="h-9 rounded-md bg-[#00305E] px-4 text-sm font-semibold text-white hover:bg-[#00305E]/90 disabled:opacity-50"
          >
            {busy ? "Merging…" : "Merge"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── QB review section (page-level) ─────────────────────────── */
function buildStudentOptions(students) {
  const out = [];
  const seen = new Set();
  for (const s of students) {
    if (!seen.has(s.student_id)) { seen.add(s.student_id); out.push({ id: s.student_id, name: s.student_name }); }
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

function QbReviewSection({ rows, year, students = [], periods = [], districts = [], onChanged, onRemoveLocal }) {
  const studentOptions = buildStudentOptions(students);
  if (!rows || rows.length === 0) return null;

  const dismiss = async (id) => {
    try { await dismissQbUnmatched(id); onRemoveLocal(id); } catch { /* ignore */ }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 shadow-sm" data-testid="qb-review-section">
      <div className="p-3 border-b border-amber-200">
        <h3 className="font-semibold text-[#00305E]">QuickBooks — Needs Review ({rows.length})</h3>
        <p className="text-xs text-muted-foreground">
          Rows from your last upload that need a person to confirm. Each is pre-filled with a best guess — confirm or fix, then Apply. Nothing here is billed until you apply it.
        </p>
      </div>
      <div className="p-3 space-y-2" data-testid="qb-unmatched-list">
        {rows.map((u) => (
          <ReviewRow
            key={u.id}
            row={u}
            year={year}
            studentOptions={studentOptions}
            periods={periods}
            districts={districts}
            onResolved={(id) => { onRemoveLocal(id); onChanged(); }}
            onDismiss={dismiss}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── QB upload dialog ─────────────────────────── */
function QbUploadDialog({ year, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const doUpload = async () => {
    if (!file) { toast.error("Choose your QuickBooks export (.xls or .xlsx) first."); return; }
    setBusy(true);
    try {
      const r = await uploadQbRegister(file, year);
      setResult(r.data);
      if (r.data.unmatched) {
        await saveQbUnmatched({ school_year: year, unmatched: r.data.unmatched });
      }
      toast.success(`${r.data.updated + r.data.seeded} matched · ${r.data.ignored} ignored · ${r.data.unmatched_count} need review.`);
      onDone();
    } catch (e) {
      if (!e?.__readonly) toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const ignoredDetail = result?.ignored_detail || {};
  const ignoredBits = Object.entries(ignoredDetail)
    .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="qb-upload-dialog">
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-[#00305E]">Upload QuickBooks Sales Register</h3>
          <button onClick={onClose} data-testid="qb-close-btn" className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Reads your QuickBooks export (.xls or .xlsx). Matches each invoice to the right student &amp; district by the
          sub-account initials (e.g. <span className="font-mono">WSD (AalBu)</span>), fills in status/paid, and applies
          per-student payments. Accounting journal entries and voids are ignored. Anything it can't place with confidence
          lands in <b>Needs Review</b> on the dashboard (below), pre-filled so you can confirm or fix it — never dismissed automatically.
        </p>
        <div className="flex items-center gap-3 mb-4">
          <input ref={inputRef} type="file" accept=".xlsx,.xls" data-testid="qb-file-input"
                 onChange={(e) => setFile(e.target.files[0])} className="text-sm" />
          <button onClick={doUpload} disabled={busy} data-testid="qb-upload-submit"
                  className="h-9 rounded-md bg-[#00305E] px-4 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? "Uploading…" : "Upload & Match"}
          </button>
        </div>
        {result && (
          <div className="mb-2 rounded-lg bg-green-50 border border-green-200 p-3 text-sm" data-testid="qb-result">
            <b>{result.updated}</b> updated · <b>{result.seeded}</b> newly matched · <b>{result.ignored}</b> ignored
            {ignoredBits ? ` (${ignoredBits})` : ""} · <b>{result.unmatched_count}</b> need review.
            <div className="mt-1 text-xs text-muted-foreground">Close this window to review the flagged rows below.</div>
          </div>
        )}
      </div>
    </div>
  );
}

const REVIEW_STATUS_OPTS = ["", "F", "H", "SSS", "P"];

/* ─────────────────────────── QB undo dialog ─────────────────────────── */
function QbUndoDialog({ year, onClose, onDone }) {
  const [batches, setBatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState(null);

  const loadBatches = useCallback(() => {
    getQbBatches(year).then((r) => setBatches(r.data.batches || [])).catch(() => setBatches([]));
  }, [year]);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const undoOne = async (batch) => {
    const label = batch.filename || "this upload";
    if (!window.confirm(`Undo "${label}"? This removes every billing line it created and restores every line it changed to exactly how they were before the upload.`)) return;
    setBusy(true);
    try {
      const r = await undoQbBatch({ school_year: year, batch_id: batch.id });
      setMsg(`Undone: restored ${r.data.restored} line(s), removed ${r.data.removed} line(s).`);
      toast.success("QuickBooks upload undone.");
      loadBatches();
      onDone();
    } catch (e) {
      if (!e?.__readonly) toast.error(e?.response?.data?.detail || "Undo failed.");
    } finally { setBusy(false); }
  };

  const reverseFromFile = async () => {
    if (!file) { toast.error("Choose the sales file you uploaded first."); return; }
    if (!window.confirm(`Reverse the upload described by "${file.name}" for ${year}? Lines created for students not enrolled this year are deleted; changed lines have their QuickBooks values cleared.`)) return;
    setBusy(true);
    try {
      const r = await reverseQbUpload(file, year);
      setMsg(`Reversed ${r.data.invoices_in_file} invoice(s): cleared ${r.data.cleared} line(s), deleted ${r.data.deleted} line(s).`);
      toast.success("Upload reversed from file.");
      onDone();
    } catch (e) {
      if (!e?.__readonly) toast.error(e?.response?.data?.detail || "Reverse failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="qb-undo-dialog">
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-[#00305E]">Undo a QuickBooks Upload</h3>
          <button onClick={onClose} data-testid="qb-undo-close-btn" className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        <p className="text-sm text-muted-foreground mb-3">
          Pick a recent upload to reverse it completely — lines it created are removed and lines it changed are restored to their exact previous values.
        </p>

        <div className="space-y-2 mb-5" data-testid="qb-batch-list">
          {batches === null && <div className="text-sm text-muted-foreground">Loading…</div>}
          {batches && batches.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-muted-foreground" data-testid="qb-batch-empty">
              No tracked uploads yet for {year}. If you uploaded before this feature existed, use “Reverse from file” below.
            </div>
          )}
          {batches && batches.map((b) => {
            const s = b.summary || {};
            return (
              <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`qb-batch-${b.id}`}>
                <div className="text-sm">
                  <div className="font-semibold text-[#00305E]">{b.filename || "QuickBooks upload"}</div>
                  <div className="text-xs text-muted-foreground">
                    {b.created_at ? new Date(b.created_at).toLocaleString() : ""} · {s.seeded || 0} created · {s.updated || 0} changed
                  </div>
                </div>
                <button
                  data-testid={`qb-undo-batch-${b.id}`}
                  disabled={busy}
                  onClick={() => undoOne(b)}
                  className="h-8 shrink-0 rounded-md bg-amber-600 px-3 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                >Undo this upload</button>
              </div>
            );
          })}
        </div>

        <div className="rounded-lg border border-slate-300 bg-slate-50 p-3">
          <h4 className="font-semibold text-sm text-[#00305E] mb-1">Reverse from file (for an older upload)</h4>
          <p className="text-xs text-muted-foreground mb-2">
            If the upload you want to undo isn’t listed above (it happened before undo tracking existed), re-select the exact same QuickBooks sales file. We’ll reverse exactly the rows it contained.
          </p>
          <div className="flex items-center gap-3">
            <input type="file" accept=".xlsx,.xls" data-testid="qb-reverse-file-input"
                   onChange={(e) => setFile(e.target.files[0])} className="text-sm" />
            <button onClick={reverseFromFile} disabled={busy} data-testid="qb-reverse-submit"
                    className="h-9 shrink-0 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              {busy ? "Working…" : "Reverse this file"}
            </button>
          </div>
        </div>

        {msg && (
          <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3 text-sm" data-testid="qb-undo-result">{msg}</div>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ row, year, studentOptions, periods, districts, onResolved, onDismiss }) {
  const isDistrict = row.category === "district_payment";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Single-resolve state.
  const [studentId, setStudentId] = useState(row.suggested_student_id || "");
  const [period, setPeriod] = useState(row.suggested_period_key || "");
  const [status, setStatus] = useState(row.suggested_status || "");
  const [invoiceNo, setInvoiceNo] = useState(row.invoice_number || "");
  const [sent, setSent] = useState(row.suggested_sent !== false);
  const [paid, setPaid] = useState(row.suggested_paid_amount ?? "");
  const [payDate, setPayDate] = useState(toISO(row.date));
  const [partial, setPartial] = useState(row.suggested_partial_amount ?? "");
  // District-payment state.
  const [district, setDistrict] = useState(row.district || "");
  const [allocs, setAllocs] = useState([]);

  const money2 = (v) => money(v);

  const resolve = async () => {
    if (!studentId || !period) { toast.error("Pick a student and a period."); return; }
    setSaving(true);
    try {
      await resolveQbUnmatched({
        item_id: row.id, school_year: year, student_id: studentId, period_key: period,
        status, invoice_number: invoiceNo, sent, district: row.district,
        paid_amount: paid === "" ? null : Number(paid),
        partial_amount: status === "P" ? (partial === "" ? null : Number(partial)) : null,
        payment_date: payDate,
      });
      toast.success("Applied to the billing grid.");
      onResolved(row.id);
    } catch (e) {
      if (!e?.__readonly) toast.error(e?.response?.data?.detail || "Could not apply.");
    } finally { setSaving(false); }
  };

  const applyDistrict = async () => {
    if (!district) { toast.error("Pick a district."); return; }
    setSaving(true);
    try {
      await applyDistrictQbPayment({
        item_id: row.id, school_year: year, district,
        amount: Math.abs(Number(row.amount)), date: row.date, memo: row.memo,
      });
      toast.success(`Applied ${money2(Math.abs(row.amount))} to ${district}.`);
      onResolved(row.id);
    } catch (e) {
      if (!e?.__readonly) toast.error(e?.response?.data?.detail || "Could not apply.");
    } finally { setSaving(false); }
  };

  const saveSplit = async () => {
    const valid = allocs.filter((a) => a.student_id && a.period_key && a.paid_amount !== "");
    if (!valid.length) { toast.error("Add at least one student allocation."); return; }
    setSaving(true);
    try {
      await splitQbUnmatched({
        item_id: row.id, school_year: year,
        allocations: valid.map((a) => ({ student_id: a.student_id, period_key: a.period_key, paid_amount: Number(a.paid_amount), payment_date: toISO(row.date) })),
      });
      toast.success(`Split across ${valid.length} student(s).`);
      onResolved(row.id);
    } catch (e) {
      if (!e?.__readonly) toast.error(e?.response?.data?.detail || "Could not split.");
    } finally { setSaving(false); }
  };

  const addAlloc = () => setAllocs((a) => [...a, { student_id: "", period_key: "", paid_amount: "" }]);
  const setAlloc = (i, patch) => setAllocs((a) => a.map((x, j) => j === i ? { ...x, ...patch } : x));
  const rmAlloc = (i) => setAllocs((a) => a.filter((_, j) => j !== i));

  return (
    <div className="rounded-lg border border-slate-200" data-testid={`qb-review-${row.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 text-xs">
        <span className="w-20 text-slate-500">{row.date}</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isDistrict ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
          {isDistrict ? "district payment" : row.type}
        </span>
        <span className="flex-1 truncate" title={`${row.customer}${row.memo ? " · " + row.memo : ""}`}>
          {row.customer}{row.memo ? <span className="text-slate-400"> · {row.memo}</span> : null}
        </span>
        <span className="tabular-nums font-semibold">{money2(row.amount)}</span>
        <button onClick={() => setOpen((o) => !o)} data-testid={`qb-review-toggle-${row.id}`}
                className="rounded bg-[#00305E] px-2 py-0.5 text-white text-[11px]">{open ? "Close" : "Resolve"}</button>
        <button onClick={() => onDismiss(row.id)} data-testid={`qb-dismiss-${row.id}`}
                className="text-slate-400 hover:text-red-600">dismiss</button>
      </div>
      <div className="px-3 pb-1 text-[11px] text-amber-700">{row.reason}</div>
      {open && (
        <div className="border-t border-slate-100 p-3 bg-slate-50/60 text-xs space-y-2">
          {!isDistrict ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
              <Field label="Student">
                <select value={studentId} onChange={(e) => setStudentId(e.target.value)}
                        data-testid={`qb-student-${row.id}`} className="w-full rounded border border-slate-300 px-1 py-1">
                  <option value="">Select…</option>
                  {studentOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Period">
                <select value={period} onChange={(e) => setPeriod(e.target.value)}
                        data-testid={`qb-period-${row.id}`} className="w-full rounded border border-slate-300 px-1 py-1">
                  <option value="">Select…</option>
                  {periods.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                        data-testid={`qb-status-${row.id}`} className="w-full rounded border border-slate-300 px-1 py-1">
                  {REVIEW_STATUS_OPTS.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                </select>
              </Field>
              {status === "P" && (
                <Field label="Partial $">
                  <input type="number" value={partial} onChange={(e) => setPartial(e.target.value)}
                         className="w-full rounded border border-slate-300 px-1 py-1 text-right" />
                </Field>
              )}
              <Field label="Invoice #">
                <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                       className="w-full rounded border border-slate-300 px-1 py-1" />
              </Field>
              <Field label="Paid $">
                <input type="number" value={paid} onChange={(e) => setPaid(e.target.value)}
                       data-testid={`qb-paid-${row.id}`} className="w-full rounded border border-slate-300 px-1 py-1 text-right" />
              </Field>
              <Field label="Payment date">
                <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
                       className="w-full rounded border border-slate-300 px-1 py-1" />
              </Field>
              <Field label="Sent">
                <label className="flex items-center gap-1 h-[30px]">
                  <input type="checkbox" checked={sent} onChange={(e) => setSent(e.target.checked)} /> invoice sent
                </label>
              </Field>
              <div className="col-span-2 md:col-span-4">
                <button onClick={resolve} disabled={saving} data-testid={`qb-apply-${row.id}`}
                        className="rounded bg-[#00305E] px-3 py-1.5 text-white text-[11px] font-semibold disabled:opacity-50">
                  {saving ? "Applying…" : "Apply to grid"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-end gap-2">
                <Field label="Apply whole payment to district">
                  <select value={district} onChange={(e) => setDistrict(e.target.value)}
                          data-testid={`qb-district-${row.id}`} className="rounded border border-slate-300 px-1 py-1 min-w-[220px]">
                    <option value="">Select district…</option>
                    {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                    {district && !districts.includes(district) && <option value={district}>{district}</option>}
                  </select>
                </Field>
                <button onClick={applyDistrict} disabled={saving} data-testid={`qb-apply-district-${row.id}`}
                        className="rounded bg-[#00305E] px-3 py-1.5 text-white text-[11px] font-semibold disabled:opacity-50">
                  Apply {money2(Math.abs(row.amount))} to district
                </button>
              </div>
              <div className="border-t border-slate-200 pt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-slate-600">…or split across the students it covers</span>
                  <button onClick={addAlloc} data-testid={`qb-add-alloc-${row.id}`}
                          className="rounded border border-slate-300 px-2 py-0.5 text-[11px]">+ student</button>
                </div>
                {allocs.map((a, i) => (
                  <div key={i} className="flex items-center gap-1 mb-1">
                    <select value={a.student_id} onChange={(e) => setAlloc(i, { student_id: e.target.value })}
                            className="rounded border border-slate-300 px-1 py-1 flex-1">
                      <option value="">Student…</option>
                      {studentOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select value={a.period_key} onChange={(e) => setAlloc(i, { period_key: e.target.value })}
                            className="rounded border border-slate-300 px-1 py-1">
                      <option value="">Period…</option>
                      {periods.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    <input type="number" placeholder="$" value={a.paid_amount}
                           onChange={(e) => setAlloc(i, { paid_amount: e.target.value })}
                           className="w-24 rounded border border-slate-300 px-1 py-1 text-right" />
                    <button onClick={() => rmAlloc(i)} className="text-slate-400 hover:text-red-600 px-1">✕</button>
                  </div>
                ))}
                {allocs.length > 0 && (
                  <button onClick={saveSplit} disabled={saving} data-testid={`qb-save-split-${row.id}`}
                          className="mt-1 rounded bg-[#00305E] px-3 py-1.5 text-white text-[11px] font-semibold disabled:opacity-50">
                    {saving ? "Saving…" : "Save split"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

// QuickBooks dates come through as MM/DD/YYYY; normalise to YYYY-MM-DD for <input type=date>.
function toISO(d) {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  const m = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  return "";
}
